import { crmApi } from "@/core/api/crmApi";
import { leadsApi } from "@/core/api/leadsApi";

export type GlobalSearchResultType = "lead" | "contact" | "account";

export interface GlobalSearchResult {
  id: string;
  type: GlobalSearchResultType;
  title: string;
  subtitle: string;
  path: string;
}

export interface GlobalSearchAccess {
  canSearchLeads: boolean;
  canSearchContacts: boolean;
  canSearchAccounts: boolean;
}

export interface GlobalSearchResponse {
  items: GlobalSearchResult[];
  failedSources: number;
}

const take = 5;

const pathWithSearch = (
  path: string,
  search: string,
  selection?: { key: string; id: string },
): string => {
  const params = new URLSearchParams({ search });
  if (selection) params.set(selection.key, selection.id);
  return `${path}?${params}`;
};

const leadTitle = (lead: {
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  mobile: string | null;
  email: string | null;
}): string =>
  lead.companyName ||
  [lead.firstName, lead.lastName].filter(Boolean).join(" ") ||
  lead.mobile ||
  lead.email ||
  "سرنخ بدون نام";

const contactTitle = (contact: {
  firstName: string | null;
  lastName: string | null;
  contactPoints: Array<{ value: string }>;
}): string =>
  [contact.firstName, contact.lastName].filter(Boolean).join(" ") ||
  contact.contactPoints[0]?.value ||
  "مخاطب بدون نام";

export const globalSearchApi = {
  async search(
    search: string,
    access: GlobalSearchAccess,
  ): Promise<GlobalSearchResponse> {
    const term = search.trim();
    if (term.length < 2) return { items: [], failedSources: 0 };

    const requests: Array<Promise<GlobalSearchResult[]>> = [];
    if (access.canSearchLeads) {
      requests.push(
        leadsApi
          .list({ page: 1, pageSize: take, search: term })
          .then((response) =>
            response.items.map((lead) => ({
              id: lead.id,
              type: "lead" as const,
              title: leadTitle(lead),
              subtitle: ["سرنخ", lead.mobile || lead.email || lead.industry]
                .filter(Boolean)
                .join(" · "),
              path: pathWithSearch("/leads", term, {
                key: "lead",
                id: lead.id,
              }),
            })),
          ),
      );
    }
    if (access.canSearchContacts) {
      requests.push(
        crmApi
          .contacts({ page: 1, pageSize: take, search: term })
          .then((response) =>
            response.items.map((contact) => ({
              id: contact.id,
              type: "contact" as const,
              title: contactTitle(contact),
              subtitle: [
                "مخاطب",
                contact.contactPoints.map((point) => point.value).join(" · "),
              ]
                .filter(Boolean)
                .join(" · "),
              path: pathWithSearch("/contacts", term, {
                key: "contact",
                id: contact.id,
              }),
            })),
          ),
      );
    }
    if (access.canSearchAccounts) {
      requests.push(
        crmApi
          .accounts({ page: 1, pageSize: take, search: term })
          .then((response) =>
            response.items.map((account) => ({
              id: account.id,
              type: "account" as const,
              title: account.name,
              subtitle: [
                "سازمان",
                account.organizationIdentifier || account.industry,
              ]
                .filter(Boolean)
                .join(" · "),
              path: pathWithSearch("/accounts", term),
            })),
          ),
      );
    }

    const settled = await Promise.allSettled(requests);
    return {
      items: settled.flatMap((result) =>
        result.status === "fulfilled" ? result.value : [],
      ),
      failedSources: settled.filter((result) => result.status === "rejected")
        .length,
    };
  },
};
